import { useQuery, useMutation, UseQueryResult, UseMutationResult, useQueryClient } from 'react-query';
import { useApiClient } from '../hooks/useApiClient';

export type ExpenseCategory = {
  id: string;
  name: string;
  description?: string | null;
  code: string;
  is_active: boolean;
  requires_receipt: boolean;
  max_amount?: string | number | null; // server may return decimal as string
  created_at: string;
  updated_at: string;
};

export type ExpenseCategoryListResponse = {
  items: ExpenseCategory[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type ListCategoriesParams = {
  page?: number;
  size?: number;
  search?: string;
  include_inactive?: boolean;
};

export type CreateCategoryPayload = {
  name: string;
  description?: string | null;
  code: string;
  is_active?: boolean;
  requires_receipt?: boolean;
  max_amount?: number | null;
};

export type UpdateCategoryPayload = Partial<Omit<CreateCategoryPayload, 'code'>>; // code immutable on update

export const buildQueryString = (params: Record<string, any>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      q.set(k, String(v));
    }
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

type ApiLike = {
  get: <T = any>(url: string, config?: any) => Promise<T>;
  post: <T = any>(url: string, data?: any, config?: any) => Promise<T>;
  put: <T = any>(url: string, data?: any, config?: any) => Promise<T>;
  delete: <T = any>(url: string, config?: any) => Promise<T>;
};

export const listAdminCategories = async (api: ApiLike, params: ListCategoriesParams = {}): Promise<ExpenseCategoryListResponse> => {
  const query = buildQueryString({
    page: params.page ?? 1,
    size: params.size ?? 20,
    search: params.search,
    include_inactive: params.include_inactive ?? true,
  });
  const data = await api.get<ExpenseCategoryListResponse>(`/admin/categories/${query}`);
  return data;
};

export const createAdminCategory = async (api: ApiLike, payload: CreateCategoryPayload): Promise<ExpenseCategory> => {
  const data = await api.post<ExpenseCategory>(`/admin/categories`, payload);
  return data;
};

export const updateAdminCategory = async (api: ApiLike, id: string, payload: UpdateCategoryPayload): Promise<ExpenseCategory> => {
  const data = await api.put<ExpenseCategory>(`/admin/categories/${id}`, payload);
  return data;
};

export const deleteAdminCategory = async (api: ApiLike, id: string): Promise<void> => {
  await api.delete(`/admin/categories/${id}`);
};

// React Query hooks
export const useAdminCategories = (params: ListCategoriesParams = {}): UseQueryResult<ExpenseCategoryListResponse> => {
  const api = useApiClient();
  return useQuery(['admin-categories', params], () => listAdminCategories(api, params), { keepPreviousData: true, staleTime: 30_000 });
};

export const useCreateAdminCategory = (): UseMutationResult<ExpenseCategory, unknown, CreateCategoryPayload> => {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation((payload) => createAdminCategory(api, payload), {
    onSuccess: () => qc.invalidateQueries('admin-categories'),
  });
};

export const useUpdateAdminCategory = (): UseMutationResult<ExpenseCategory, unknown, { id: string; payload: UpdateCategoryPayload }> => {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation(({ id, payload }) => updateAdminCategory(api, id, payload), {
    onSuccess: () => qc.invalidateQueries('admin-categories'),
  });
};

export const useDeleteAdminCategory = (): UseMutationResult<void, unknown, { id: string }> => {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation(({ id }) => deleteAdminCategory(api, id), {
    onSuccess: () => qc.invalidateQueries('admin-categories'),
  });
};
